import UIHoverCardController from "controllers/ui/hover-card_controller";
import { createPopper } from "@popperjs/core";

export default class UITooltipController extends UIHoverCardController {
  static targets = ["content", "wrapper", "trigger", "arrow"];
  static values = { onlyWhenTruncated: Boolean };

  connect() {
    // Create popper instance with arrow modifier
    this.popperInstance = createPopper(this.triggerTarget, this.contentTarget, {
      placement: this.contentTarget.dataset.side || "top",
      strategy: this.contentTarget.dataset.strategy || "absolute",
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "flip",
          options: {
            fallbackPlacements: ["bottom", "right", "left"],
          },
        },
        {
          name: "arrow",
          options: {
            element: this.arrowTarget,
            padding: 8,
          },
        },
        {
          name: "syncDataSide",
          enabled: true,
          phase: "write",
          fn: ({ state }) => {
            this.contentTarget.dataset.side = state.placement.split("-")[0];
          },
        },
      ],
    });

    // Store bound functions for proper cleanup
    this.boundHandleMouseEnter = this.handleMouseEnter.bind(this);
    this.boundHandleMouseLeave = this.handleMouseLeave.bind(this);
    this.boundStopPropagation = (e) => e.stopPropagation();

    // Bind event listeners for mouse interactions
    this.triggerTarget.addEventListener("mouseenter", this.boundHandleMouseEnter);
    this.triggerTarget.addEventListener("mouseleave", this.boundHandleMouseLeave);
    this.wrapperTarget.addEventListener("mouseenter", this.boundHandleMouseEnter);
    this.wrapperTarget.addEventListener("mouseleave", this.boundHandleMouseLeave);
    this.wrapperTarget.addEventListener("click", this.boundStopPropagation);

    // Add keyboard accessibility. focusin/focusout, not focus/blur: those do not bubble, so a trigger
    // wrapping a focusable child (a button, a link) never opened on tab.
    this.triggerTarget.addEventListener("focusin", this.boundHandleMouseEnter);
    this.triggerTarget.addEventListener("focusout", this.boundHandleMouseLeave);
  }

  disconnect() {
    // Clean up event listeners
    this.triggerTarget.removeEventListener("mouseenter", this.boundHandleMouseEnter);
    this.triggerTarget.removeEventListener("mouseleave", this.boundHandleMouseLeave);
    this.wrapperTarget.removeEventListener("mouseenter", this.boundHandleMouseEnter);
    this.wrapperTarget.removeEventListener("mouseleave", this.boundHandleMouseLeave);
    this.wrapperTarget.removeEventListener("click", this.boundStopPropagation);
    this.triggerTarget.removeEventListener("focusin", this.boundHandleMouseEnter);
    this.triggerTarget.removeEventListener("focusout", this.boundHandleMouseLeave);

    // Clear any pending leave timeout
    clearTimeout(this.leaveTimeout);

    // Destroy popper instance
    if (this.popperInstance) {
      this.popperInstance.destroy();
    }
  }

  get suppressed() {
    if (!this.onlyWhenTruncatedValue) return false;

    const clipped = (el) => el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
    return !clipped(this.triggerTarget) && ![...this.triggerTarget.children].some(clipped);
  }

  handleMouseEnter() {
    clearTimeout(this.leaveTimeout);
    if (this.suppressed) return;

    this.mouseEnter();
    // Force Popper to recalculate position after tooltip becomes visible
    if (this.popperInstance) {
      this.popperInstance.update();
    }
  }

  handleMouseLeave() {
    this.leaveTimeout = setTimeout(() => this.mouseLeave(), 100);
  }
}
